import React, { useState } from "react";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import emailjs from "emailjs-com";
import { ToastContainer, toast } from "react-toastify";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useSelector } from "react-redux";
import "../css/SharedForms.css";

const DISCORD_WEBHOOK =
  "https://discord.com/api/webhooks/1505968721569644775/9GusFvxpE2rdsXLhXQV5jTvNqdwFh1jiPhNxi8IInR3Pd7xLYceeneRi7eYpfzkBah_J";

const TERMS = [
  {
    title: "Volunteer Work",
    desc: "I will work without salary. This is non-profit service.",
    icon: "💼",
  },
  {
    title: "Stay Active",
    desc: "Attend 1 meeting/month. Online or offline.",
    icon: "📅",
  },
  {
    title: "No Discrimination",
    desc: "We serve all, without bias of religion or caste.",
    icon: "🤝",
  },
  {
    title: "Keep Your Word",
    desc: "Finish any task you say yes to.",
    icon: "✅",
  },
  {
    title: "No Misuse",
    desc: "Don't use the Foundation's name for personal/political work.",
    icon: "🚫",
  },
  {
    title: "Agree & Sign",
    desc: "Sign + date to confirm you've read and accept these terms.",
    icon: "✍️",
  },
];

const VolunteerAgreementForm = () => {
  const currentTheme = useSelector((state) => state.theme.current);

  const [formData, setFormData] = useState({
    fullName: "",
    whatsappNumber: "",
    contactNumber: "",
    emailAddress: "",
    fullAddress: "",
    aadharNumber: "",
    agreed: false,
    signatureDate: new Date().toISOString().split("T")[0],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fullName.trim() || formData.fullName.trim().length < 2)
      newErrors.fullName = "Full name is required (min 2 characters)";
    if (!/^\+?[\d\s-]{10,}$/.test(formData.whatsappNumber))
      newErrors.whatsappNumber = "Valid WhatsApp number is required";
    if (!/^\+?[\d\s-]{10,}$/.test(formData.contactNumber))
      newErrors.contactNumber = "Valid contact number is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress))
      newErrors.emailAddress = "Valid email address is required";
    if (!formData.fullAddress.trim() || formData.fullAddress.trim().length < 5)
      newErrors.fullAddress = "Full address is required (min 5 characters)";
    if (!/^\d{12}$/.test(formData.aadharNumber.replace(/\s/g, "")))
      newErrors.aadharNumber = "Valid 12-digit Aadhar number is required";
    if (!formData.agreed)
      newErrors.agreed = "You must agree to all terms and conditions";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill in all fields correctly", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    const body = `
AQWF VOLUNTEER AGREEMENT FORM
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Full Name: ${formData.fullName}
WhatsApp Number: ${formData.whatsappNumber}
Contact Number: ${formData.contactNumber}
Email Address: ${formData.emailAddress}
Full Address: ${formData.fullAddress}
Aadhar Number: ${formData.aadharNumber}
Agreed to Terms: Yes
Signature Date: ${formData.signatureDate}
Submitted At: ${new Date().toLocaleString()}
    `.trim();

    try {
      await addDoc(collection(db, "VolunteerAgreements"), {
        date: Timestamp.now(),
        formType: "volunteer-agreement",
        status: "pending",
        fullName: formData.fullName,
        whatsappNumber: formData.whatsappNumber,
        contactNumber: formData.contactNumber,
        emailAddress: formData.emailAddress,
        fullAddress: formData.fullAddress,
        aadharNumber: formData.aadharNumber,
        agreed: true,
        signatureDate: formData.signatureDate,
        details: body,
        bookedBy: "",
      });
    } catch (err) {
      console.error("Firestore error:", err);
    }

    try {
      await fetch(DISCORD_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          embeds: [
            {
              title: "🙋 New Volunteer Agreement Form Submitted",
              color: 0x27ae60,
              description: body.slice(0, 4000),
              timestamp: new Date(),
            },
          ],
        }),
      });
    } catch (err) {
      console.error("Discord error:", err);
    }

    emailjs
      .send(
        "service_y5g4vjs",
        "template_1ayop5o",
        {
          to_email: "alqaimwelfareinfo@gmail.com",
          from_name: "Al Qaim Welfare Foundation - Volunteer Agreement",
          subject: `Volunteer Agreement - ${formData.fullName} - ${formData.contactNumber}`,
          message: body,
        },
        "ivuz1YIJqTWXipbyc"
      )
      .then(() => {
        toast.success(
          "Your volunteer agreement has been submitted! We will contact you soon.",
          { position: "top-center", autoClose: 4000 }
        );
        setFormData({
          fullName: "",
          whatsappNumber: "",
          contactNumber: "",
          emailAddress: "",
          fullAddress: "",
          aadharNumber: "",
          agreed: false,
          signatureDate: new Date().toISOString().split("T")[0],
        });
        setErrors({});
        setIsSubmitting(false);
      })
      .catch((err) => {
        console.error("EmailJS error:", err);
        toast.error("Failed to send. Please try again.", {
          position: "top-center",
          autoClose: 3000,
        });
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <Navbar />
      <div className="sf-page">
        <div className="sf-hero sf-hero-green" style={{ background: currentTheme }}>
          <div className="sf-hero-icon">🙋</div>
          <h1 className="sf-hero-title">AQWF Volunteer Agreement</h1>
          <p className="sf-hero-sub">
            Register as a volunteer and help us serve the community
          </p>
        </div>

        <div className="sf-container">
          {/* Terms Card */}
          <div className="sf-instructions-card sf-terms-card">
            <h2 className="sf-instructions-title">
              <span className="sf-inst-icon">📜</span> Volunteer Terms & Conditions
            </h2>
            <div className="sf-terms-grid">
              {TERMS.map((term, i) => (
                <div key={i} className="sf-term-item">
                  <span className="sf-term-icon">{term.icon}</span>
                  <div>
                    <strong className="sf-term-title">{term.title}</strong>
                    <p className="sf-term-desc">{term.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <div className="sf-form-card">
            <h2 className="sf-form-title">Volunteer Registration</h2>
            <form className="sf-form" onSubmit={handleSubmit} noValidate>
              <div className="sf-field">
                <label className="sf-label">
                  Full Name <span className="sf-required">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  className={`sf-input ${errors.fullName ? "sf-input-error" : ""}`}
                  placeholder="Enter your full name"
                  value={formData.fullName}
                  onChange={handleChange}
                />
                {errors.fullName && (
                  <span className="sf-err-msg">{errors.fullName}</span>
                )}
              </div>

              <div className="sf-row">
                <div className="sf-field">
                  <label className="sf-label">
                    WhatsApp Number <span className="sf-required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="whatsappNumber"
                    className={`sf-input ${errors.whatsappNumber ? "sf-input-error" : ""}`}
                    placeholder="e.g. 9876543210"
                    value={formData.whatsappNumber}
                    onChange={handleChange}
                  />
                  {errors.whatsappNumber && (
                    <span className="sf-err-msg">{errors.whatsappNumber}</span>
                  )}
                </div>

                <div className="sf-field">
                  <label className="sf-label">
                    Contact Number <span className="sf-required">*</span>
                  </label>
                  <input
                    type="tel"
                    name="contactNumber"
                    className={`sf-input ${errors.contactNumber ? "sf-input-error" : ""}`}
                    placeholder="e.g. 9876543210"
                    value={formData.contactNumber}
                    onChange={handleChange}
                  />
                  {errors.contactNumber && (
                    <span className="sf-err-msg">{errors.contactNumber}</span>
                  )}
                </div>
              </div>

              <div className="sf-field">
                <label className="sf-label">
                  Email Address <span className="sf-required">*</span>
                </label>
                <input
                  type="email"
                  name="emailAddress"
                  className={`sf-input ${errors.emailAddress ? "sf-input-error" : ""}`}
                  placeholder="Enter your email address"
                  value={formData.emailAddress}
                  onChange={handleChange}
                />
                {errors.emailAddress && (
                  <span className="sf-err-msg">{errors.emailAddress}</span>
                )}
              </div>

              <div className="sf-field">
                <label className="sf-label">
                  Full Address <span className="sf-required">*</span>
                </label>
                <textarea
                  name="fullAddress"
                  className={`sf-textarea ${errors.fullAddress ? "sf-input-error" : ""}`}
                  placeholder="Enter your complete address"
                  value={formData.fullAddress}
                  onChange={handleChange}
                  rows={3}
                />
                {errors.fullAddress && (
                  <span className="sf-err-msg">{errors.fullAddress}</span>
                )}
              </div>

              <div className="sf-field">
                <label className="sf-label">
                  Aadhar Number <span className="sf-required">*</span>
                </label>
                <input
                  type="text"
                  name="aadharNumber"
                  className={`sf-input ${errors.aadharNumber ? "sf-input-error" : ""}`}
                  placeholder="Enter 12-digit Aadhar number"
                  value={formData.aadharNumber}
                  onChange={handleChange}
                  maxLength={14}
                />
                {errors.aadharNumber && (
                  <span className="sf-err-msg">{errors.aadharNumber}</span>
                )}
              </div>

              <div className="sf-field">
                <label className="sf-label">Signature Date</label>
                <input
                  type="date"
                  name="signatureDate"
                  className="sf-input"
                  value={formData.signatureDate}
                  onChange={handleChange}
                />
              </div>

              <div
                className={`sf-checkbox-card ${errors.agreed ? "sf-checkbox-error" : ""}`}
              >
                <label className="sf-checkbox-label">
                  <input
                    type="checkbox"
                    name="agreed"
                    className="sf-checkbox"
                    checked={formData.agreed}
                    onChange={handleChange}
                  />
                  <span className="sf-checkbox-text">
                    ✅ I have read, understood, and agree to all the volunteer
                    terms and conditions listed above. I confirm that I am
                    joining Al-Qaim Welfare Foundation voluntarily as a
                    non-salaried volunteer for non-profit community service.
                  </span>
                </label>
                {errors.agreed && (
                  <span className="sf-err-msg">{errors.agreed}</span>
                )}
              </div>

              <button
                type="submit"
                className="sf-submit-btn"
                style={{ background: currentTheme }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="sf-spinner"></span> Submitting...
                  </>
                ) : (
                  "Submit Agreement"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
      <Footer />
    </>
  );
};

export default VolunteerAgreementForm;